import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Flex,
  FormField,
  Grid,
  Icon,
  Modal,
  PhoneInput,
  Spacer,
  TextInput,
  Typography
} from '@percent/lemonade'
import {
  useAuthState,
  useCausesPortalAnalytics,
  useIsOrganisationInactive,
  useMutation
} from '@percent/cause-dashboard/common/hooks'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { HELP_LINK } from '@percent/cause-dashboard/constants/support'
import { getSmartApplyPartnerConfigData, smartApplyPartners } from '@percent/cause-dashboard/constants/smartApplyPartners'
import type { ValidationSubmissionPartnerFields } from '@percent/cause-dashboard/services/applications/validationSubmission'
import { phoneRegExp } from '@percent/cause-dashboard/common/utility/validation'
import { useApplicationsCaptcha } from '@percent/cause-dashboard/common/hooks/useApplicationsCaptcha/useApplicationsCaptcha'
import { CaptchaWidget } from '@percent/cause-dashboard/common/components/captchaWidget/CaptchaWidget'
import { CaptchaSlowWarning } from '@percent/cause-dashboard/common/components/captchaWidget/CaptchaSlowWarning'
import type { NativeApplyOpenArgs } from './NativeApplyModal.types'
import styles from './NativeApplyModal.module.scss'

export type { NativeApplyOpenArgs, NativeApplyTracking } from './NativeApplyModal.types'

export type NativeApplyModalProps = {
  open: boolean
  onClose: () => void
  args: NativeApplyOpenArgs
}

const SCREEN_STATE = {
  Initial: 'initial',
  Success: 'success',
  Error: 'error'
} as const

type ScreenState = typeof SCREEN_STATE[keyof typeof SCREEN_STATE]

function splitFullName(fullName: string | undefined | null): { firstName: string; lastName: string } {
  const trimmed = fullName?.trim() ?? ''
  if (!trimmed) {
    return { firstName: '', lastName: '' }
  }
  const parts = trimmed.split(/\s+/)
  return {
    firstName: parts[0] ?? '',
    lastName: parts.slice(1).join(' ').trim()
  }
}

export function NativeApplyModal({ open, onClose, args }: Readonly<NativeApplyModalProps>) {
  const { t } = useTranslation()
  const { authState } = useAuthState()
  const { track } = useCausesPortalAnalytics()
  const { storeService } = useServices()
  const isOrganisationInactive = useIsOrganisationInactive()

  const { partnerKey, modalContext } = args
  const partnerName = modalContext.title
  const requiredFields = smartApplyPartners[partnerKey].partnerFields

  const profileNames = useMemo(() => splitFullName(authState.user?.fullName), [authState.user?.fullName])
  const profilePhone = authState.user?.phoneNumber?.trim() ?? ''

  const [firstName, setFirstName] = useState(profileNames.firstName)
  const [lastName, setLastName] = useState(profileNames.lastName)
  const [phoneNumber, setPhoneNumber] = useState(profilePhone)
  const [firstNameTouched, setFirstNameTouched] = useState(false)
  const [lastNameTouched, setLastNameTouched] = useState(false)
  const [phoneTouched, setPhoneTouched] = useState(false)
  const [screenState, setScreenState] = useState<ScreenState>(SCREEN_STATE.Initial)
  const trackedOpenRef = useRef(false)

  useEffect(() => {
    if (!open) {
      return
    }
    setFirstName(profileNames.firstName)
    setLastName(profileNames.lastName)
    setPhoneNumber(profilePhone)
    setFirstNameTouched(false)
    setLastNameTouched(false)
    setPhoneTouched(false)
    setScreenState(SCREEN_STATE.Initial)
    trackedOpenRef.current = false
  }, [open, profileNames.firstName, profileNames.lastName, profilePhone])

  const showFirstName = !profileNames.firstName
  const showLastName = !profileNames.lastName
  const showPhone = requiredFields.phone && !profilePhone

  const firstNameError =
    showFirstName && firstNameTouched && !firstName.trim()
      ? t('form.isRequired', { name: t('form.firstName') })
      : undefined
  const lastNameError =
    showLastName && lastNameTouched && !lastName.trim() ? t('form.isRequired', { name: t('form.lastName') }) : undefined
  const phoneError = (() => {
    if (!showPhone || !phoneTouched) return undefined
    const value = phoneNumber.trim()
    if (!value) return t('form.isRequired', { name: t('form.phoneNumber') })
    if (!phoneRegExp.test(value)) return t('errorMessage.notValidPhoneNumber')
    return undefined
  })()

  const isFormValid =
    (!showFirstName || firstName.trim().length > 0) &&
    (!showLastName || lastName.trim().length > 0) &&
    (!showPhone || (phoneNumber.trim().length > 0 && phoneRegExp.test(phoneNumber.trim())))

  const { recaptchaRef, getCaptchaToken, isVerifying, isSlow } = useApplicationsCaptcha()

  const [{ isLoading }, { apiFunc: createValidationSubmissions }] = useMutation(
    storeService.createValidationSubmissions,
    () => {
      setScreenState(SCREEN_STATE.Success)
    },
    () => {
      setScreenState(SCREEN_STATE.Error)
    }
  )

  const handleSubmit = useCallback(async () => {
    // Defensive type-narrow: the modal is only mounted for authenticated users so this branch is
    // not expected to fire in practice; we just bail out without further action.
    if (!authState.user?.id) {
      return
    }

    if (isOrganisationInactive) {
      setScreenState(SCREEN_STATE.Error)
      return
    }

    if (!isFormValid) {
      setFirstNameTouched(true)
      setLastNameTouched(true)
      setPhoneTouched(true)
      return
    }

    track('Native Apply Modal Applied', {
      partner: partnerKey,
      organisation: authState.claimOrganisation?.displayName,
      organisationId: authState.claimOrganisation?.id,
      accountId: authState.user.id,
      programmeOfferName: args?.tracking?.programmeOfferName,
      source: args?.tracking?.source,
      originCategory: args?.tracking?.originCategory,
      page: args?.tracking?.page
    })

    const partnerFields: ValidationSubmissionPartnerFields = {}
    if (requiredFields.phone) {
      const phoneValue = (phoneNumber || profilePhone).trim()
      if (phoneValue) {
        partnerFields.phone = phoneValue
      }
    }
    const partnerFieldsPayload = Object.keys(partnerFields).length > 0 ? partnerFields : undefined

    createValidationSubmissions({
      accountId: authState.user.id,
      organisationId: authState.claimOrganisation?.id,
      partners: [getSmartApplyPartnerConfigData(partnerKey)],
      names: {
        firstName: (firstName || profileNames.firstName).trim(),
        lastName: (lastName || profileNames.lastName).trim()
      },
      partnerFields: partnerFieldsPayload,
      source: 'native_apply',
      token: await getCaptchaToken()
    })
  }, [
    partnerKey,
    authState.user?.id,
    authState.claimOrganisation?.displayName,
    authState.claimOrganisation?.id,
    isFormValid,
    isOrganisationInactive,
    track,
    args.tracking?.programmeOfferName,
    args.tracking?.source,
    args.tracking?.originCategory,
    args.tracking?.page,
    createValidationSubmissions,
    getCaptchaToken,
    firstName,
    lastName,
    profileNames.firstName,
    profileNames.lastName,
    requiredFields.phone,
    phoneNumber,
    profilePhone
  ])

  useEffect(() => {
    if (!open || trackedOpenRef.current) {
      return
    }
    trackedOpenRef.current = true
    track('Native Apply Modal Shown', {
      partner: partnerKey,
      organisation: authState.claimOrganisation?.displayName,
      organisationId: authState.claimOrganisation?.id,
      accountId: authState.user?.id,
      programmeOfferName: args.tracking?.programmeOfferName,
      source: args.tracking?.source,
      originCategory: args.tracking?.originCategory,
      page: args.tracking?.page
    })
  }, [
    open,
    partnerKey,
    track,
    authState.claimOrganisation?.displayName,
    authState.claimOrganisation?.id,
    authState.user?.id,
    args.tracking?.programmeOfferName,
    args.tracking?.source,
    args.tracking?.originCategory,
    args.tracking?.page
  ])

  if (!open) {
    return null
  }

  const isInitial = isLoading || screenState === SCREEN_STATE.Initial
  const isSuccess = !isLoading && screenState === SCREEN_STATE.Success
  const isError = !isLoading && screenState === SCREEN_STATE.Error
  const isBlocked = isInitial && isOrganisationInactive

  return (
    <Modal open={open} onClose={onClose} width={520} data-testid="native-apply-modal">
      <div className={styles.nativeApplyModalContainer}>
        <button
          type="button"
          aria-label={t('button.close')}
          className={styles.closeButton}
          data-testid="native-apply-modal-close"
          onClick={onClose}
        >
          <Icon name="close" size={4} color="gray400" />
        </button>

        <div className={styles.contentWrapper}>
          <Flex align="center" gap={12} mb={16} pr={32}>
            <img src={modalContext.logoUrl} alt={partnerName} className={styles.headerLogo} />
            <Typography variant="h3" className={styles.title}>
              {partnerName}
            </Typography>
          </Flex>

          {isBlocked && (
            <>
              <div className={styles.alertWrapper}>
                <Alert variant="warning" title={t('typography.nativeApply.inactiveOrg.title')}>
                  {t('typography.nativeApply.inactiveOrg.description')}
                </Alert>
              </div>
              <Typography color="textSecondary">
                {t('typography.nativeApply.helpCenter.text')}{' '}
                <a href={HELP_LINK} target="_blank" rel="noopener noreferrer">
                  {t('typography.nativeApply.helpCenter.urlText')}
                </a>
              </Typography>
            </>
          )}

          {isInitial && !isBlocked && (
            <>
              <div className={styles.description}>
                <Typography variant="default">{modalContext.description}</Typography>
              </div>

              <Flex direction="column" gap={16}>
                {(showFirstName || showLastName) && (
                  <Grid gutter={16}>
                    {showFirstName && (
                      <Grid.Col span={6}>
                        <FormField
                          label={t('form.firstName')}
                          status={firstNameError ? 'danger' : 'default'}
                          statusMessage={firstNameError}
                          necessity="required"
                        >
                          <TextInput
                            type="text"
                            name="firstName"
                            placeholder={t('form.firstNameDescription')}
                            value={firstName}
                            onChange={e => setFirstName(e.target.value)}
                            onBlur={() => setFirstNameTouched(true)}
                            disabled={isLoading}
                          />
                        </FormField>
                      </Grid.Col>
                    )}
                    {showLastName && (
                      <Grid.Col span={6}>
                        <FormField
                          label={t('form.lastName')}
                          status={lastNameError ? 'danger' : 'default'}
                          statusMessage={lastNameError}
                          necessity="required"
                        >
                          <TextInput
                            type="text"
                            name="lastName"
                            placeholder={t('form.lastNameDescription')}
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                            onBlur={() => setLastNameTouched(true)}
                            disabled={isLoading}
                          />
                        </FormField>
                      </Grid.Col>
                    )}
                  </Grid>
                )}

                {showPhone && (
                  <FormField
                    label={t('form.phoneNumber')}
                    status={phoneError ? 'danger' : 'default'}
                    statusMessage={phoneError}
                    necessity="required"
                  >
                    <div onBlur={() => setPhoneTouched(true)}>
                      <PhoneInput
                        name="phoneNumber"
                        international
                        placeholder=""
                        value={phoneNumber}
                        onChange={value => {
                          setPhoneNumber(value ?? '')
                        }}
                      />
                    </div>
                  </FormField>
                )}
              </Flex>
            </>
          )}

          {isSuccess && (
            <>
              <div className={styles.alertWrapper}>
                <Alert variant="success" title={t('typography.nativeApply.success')} />
              </div>
              <Typography color="textSecondary" variant="default-bold" className={styles.whatHappensNowTitle}>
                {t('typography.nativeApply.whatHappensNow.title')}
              </Typography>
              <Typography color="textSecondary" className={styles.hangingIndent}>
                {t('typography.nativeApply.whatHappensNow.bullet1')}
              </Typography>
              <Typography color="textSecondary" className={styles.hangingIndent}>
                <Box
                  dangerouslySetInnerHTML={{
                    __html: t('typography.nativeApply.whatHappensNow.bullet2')
                  }}
                />
              </Typography>
              <Typography color="textSecondary" className={styles.hangingIndent}>
                {t('typography.nativeApply.whatHappensNow.bullet3')}
              </Typography>
              <Typography color="textSecondary" className={styles.remember}>
                {t('typography.nativeApply.remember')}
              </Typography>
            </>
          )}

          {isError && (
            <>
              <div className={styles.alertWrapper}>
                <Alert variant="default" title={t('typography.nativeApply.oops')} />
              </div>
              <Typography color="textSecondary">
                {t('typography.nativeApply.helpCenter.text')}{' '}
                <a href={HELP_LINK} target="_blank" rel="noopener noreferrer">
                  {t('typography.nativeApply.helpCenter.urlText')}
                </a>
              </Typography>
            </>
          )}
        </div>

        <div className={styles.footer}>
          {isInitial && (
            <>
              {!isBlocked && (
                <>
                  <p className={styles.disclaimer}>
                    {t('typography.nativeApply.disclaimer', { partner: partnerName })}
                  </p>
                  <Spacer size={2} axis="vertical" />
                </>
              )}
              <Button
                loading={isLoading || isVerifying}
                loadingText={t('button.applying')}
                variant="primary"
                stretch
                onClick={handleSubmit}
                disabled={!isFormValid || isBlocked || isVerifying}
                data-testid="native-apply-modal-submit"
              >
                {t('button.apply')}
              </Button>
              <CaptchaSlowWarning isSlow={isSlow} />
            </>
          )}
          {isSuccess && (
            <Typography color="textSecondary" className={styles.helpLink} data-testid="native-apply-modal-help-link">
              {t('typography.nativeApply.helpCenter.text')}{' '}
              <a href={HELP_LINK} target="_blank" rel="noopener noreferrer">
                {t('typography.nativeApply.helpCenter.urlText')}
              </a>
            </Typography>
          )}
          {isError && (
            <Button variant="primary" stretch onClick={onClose} data-testid="native-apply-modal-close-footer">
              {t('button.close')}
            </Button>
          )}

          <CaptchaWidget ref={recaptchaRef} />
        </div>
      </div>
    </Modal>
  )
}
